// ReachPanel.jsx
//
// Live audience estimate panel. Takes a ruleTree, debounces 500ms, queries
// user_profiles_marketing via segmentResolver, renders match count + reachable
// count + language/city breakdowns.
//
// Used by:
//   - segmentEditor.js (right rail)
//   - campaignEditor.js (audience panel)
//
// See MARKETING_CAMPAIGNS_PLAN.md §2 (Reach preview).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import { firestore, functions } from '../../../../../helpers/Firebase';
import { previewReach, validateRuleTree } from '../helpers/segmentResolver.js';

const DEBOUNCE_MS = 500;
const SCAN_CAP = 5000;

// Project theme overrides Bootstrap .badge to display:block — use inline pills.
const pill = (bg, fg = '#fff') => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 10,
  background: bg,
  color: fg,
  fontSize: '0.75rem',
  fontWeight: 500,
  marginRight: 4,
  marginBottom: 4,
});

const ReachPanel = ({ ruleTree, title = 'Audience reach' }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const timerRef = useRef(null);
  const reqIdRef = useRef(0);

  const treeKey = useMemo(() => {
    try { return JSON.stringify(ruleTree); } catch { return ''; }
  }, [ruleTree]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setError(null);

    if (!ruleTree || !ruleTree.rules || ruleTree.rules.length === 0) {
      setResult(null);
      setValidationErrors([]);
      return;
    }

    const errors = validateRuleTree(ruleTree);
    if (errors.length) {
      setValidationErrors(errors);
      setResult(null);
      return;
    }
    setValidationErrors([]);

    timerRef.current = setTimeout(async () => {
      const myReq = ++reqIdRef.current;
      setLoading(true);
      try {
        // Prefer the server-side callable — routes to Data Connect (unlimited
        // scan) or Firestore based on settings.dataConnect.readsEnabled. Falls
        // back to the client-side scanner if the callable isn't deployed yet
        // or fails, so the panel keeps working through the SQL cutover window.
        let r;
        try {
          const call = functions().httpsCallable('queryMarketingSegment');
          const resp = await call({ ruleTree });
          r = resp?.data;
        } catch (callableErr) {
          console.warn('[ReachPanel] queryMarketingSegment unavailable, falling back to client scan', callableErr?.message);
          r = await previewReach(
            firestore().collection('user_profiles_marketing'),
            ruleTree,
            { limit: SCAN_CAP, enforceReachability: true }
          );
        }
        if (myReq !== reqIdRef.current) return;
        setResult(r);
      } catch (e) {
        if (myReq === reqIdRef.current) setError(e.message);
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [treeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="mb-3">
      <CardBody>
        <h6 className="mb-3">{title}</h6>

        {validationErrors.length > 0 && (
          <div className="text-warning">
            <small>
              <strong>Fix these to see reach:</strong>
              <ul className="mb-0 mt-1 pl-3">
                {validationErrors.slice(0, 3).map((e, i) => <li key={i}>{e}</li>)}
                {validationErrors.length > 3 && <li>… {validationErrors.length - 3} more</li>}
              </ul>
            </small>
          </div>
        )}

        {!validationErrors.length && error && (
          <div className="text-danger"><small>Error: {error}</small></div>
        )}

        {!validationErrors.length && !error && !result && !loading && (
          <div className="text-muted"><small>Add a rule to see audience size.</small></div>
        )}

        {loading && (
          <div className="text-muted"><small>Counting…</small></div>
        )}

        {result && !loading && (
          <>
            <div className="d-flex align-items-baseline mb-1">
              <span style={{ fontSize: '2rem', fontWeight: 600, marginRight: 8 }}>
                {result.matchedCount.toLocaleString()}
              </span>
              <span className="text-muted"><small>matched users</small></span>
            </div>

            <div className="text-muted small mb-3">
              Scanned {result.candidateCount.toLocaleString()} profiles · strategy: {result.strategy}
              {result.hitLimit && (
                <div className="mt-1">
                  <span style={pill('#ffc107', '#212529')}>
                    Capped at {SCAN_CAP.toLocaleString()} — actual count may be higher
                  </span>
                </div>
              )}
            </div>

            {Object.keys(result.byLanguage).length > 0 && (
              <div className="mb-3">
                <div className="text-muted small mb-1">By language</div>
                <div>
                  {Object.entries(result.byLanguage).map(([lang, n]) => (
                    <span style={pill('#e9ecef', '#495057')} key={lang}>
                      {lang}: {n.toLocaleString()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {result.topEmirates && result.topEmirates.length > 0 && (
              <div className="mb-3">
                <div className="text-muted small mb-1">
                  Top emirates <span className="text-muted" style={{ fontWeight: 400 }}>
                    ({result.emirateBreakdownConstrained ? 'matches your rule' : 'any saved place'})
                  </span>
                </div>
                <div>
                  {result.topEmirates.map(({ emirate, count }) => (
                    <span style={pill('#e9ecef', '#495057')} key={emirate}>
                      {emirate}: {count.toLocaleString()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Top cities breakdown hidden — city fields no longer exposed in the picker. */}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default ReachPanel;
